/**
 * Υπάλληλοι του καταστήματος (απόφαση ιδιοκτήτη 12/10/2026): ρόλοι, PIN και δικαιώματα ανά άτομο.
 * Καθαρές συναρτήσεις — η οθόνη (EmployeesPanel) και τα tests τις μοιράζονται.
 *
 * Δικαιώματα: ίδια κλειδιά, ενότητες και κανόνες με την παλιά οθόνη «Δικαιώματα Προσωπικού» (καταργήθηκε
 * 12/10/2026, ζει πια εδώ) και με τον server (middleware/dineInPermissions, hooks/usePermissions):
 * «defaultTrue» = ανοιχτό εκτός αν είναι ρητά false, τα υπόλοιπα μόνο με ρητό true.
 */

export type Perms = Record<string, unknown>;

export interface RoleOption {
  id: number;
  name: string;
  display_name: string;
  description?: string | null;
  /** Προεπιλογές του ρόλου (μόνο ?for=employee_form, για διαχειριστή). */
  permissions?: Perms | null;
}

export interface StoreEmployee {
  id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  is_active: boolean;
  created_at?: string;
  kiosk_terminal_id?: number | null;
  role_id: number;
  role_name: string;
  role_display_name: string;
  pin_length?: number | null;
  has_pin?: boolean;
  /** Ρόλου + ατόμου (όπως το models/Employee.js). */
  permissions?: Perms | null;
  /** Μόνο του ατόμου (employees.permissions)· null = του ρόλου. */
  permission_overrides?: Perms | null;
  has_custom_permissions?: boolean;
}

export interface StoreFlags {
  dine_in?: boolean;
  kiosk_enabled?: boolean;
  /** Πιάτα ημέρας (migration 278): μόνο τότε φαίνεται το δικαίωμα «Πιάτα ημέρας». */
  daily_dishes_enabled?: boolean;
}

/** Οι ρόλοι της φόρμας, με τη σειρά τους. Κουζίνα μόνο με τραπέζια, kiosk μόνο με kiosk. */
export const OFFERED_ROLES = ['store_admin', 'cashier', 'waiter', 'kitchen', 'kiosk'] as const;
/** Καταργήθηκαν (migration 267): φαίνονται, αλλά μόνο για να αλλάξουν. */
export const LEGACY_ROLES = ['senior_waiter', 'manager'];
/** Λογαριασμοί κεντρικών (κατάστημα 1): εδώ μόνο ανάγνωση — τους αλλάζει το Super Admin. */
export const HQ_ROLES = ['super_admin', 'support', 'org_admin', 'sales'];
export const DEFAULT_ROLE = 'cashier';
/** Ρόλοι με ρυθμιζόμενα δικαιώματα· ο διαχειριστής τα έχει όλα. */
export const PERMISSION_ROLES = ['waiter', 'cashier'];
const ROLE_ORDER = [...OFFERED_ROLES, 'driver', ...LEGACY_ROLES, ...HQ_ROLES] as string[];

/** Διανομείς (απόφαση ιδιοκτήτη 12/10/2026): χωρίς σύνδεση, ζουν στο Delivery → «Διανομείς», όχι εδώ. */
export const DRIVER_ROLE = 'driver';
export const listedEmployees = (list: StoreEmployee[]) => list.filter(e => e.role_name !== DRIVER_ROLE);

export const isOfferedRole = (name?: string | null) => (OFFERED_ROLES as readonly string[]).includes(name || '');
export const isLegacyRole = (name?: string | null) => LEGACY_ROLES.includes(name || '');
export const isHqRole = (name?: string | null) => HQ_ROLES.includes(name || '');

/** Όσα ψηφία ζητά η οθόνη σύνδεσης (PinEntry: pin_length): 6 ο διαχειριστής, 4 οι άλλοι. */
export const pinLengthFor = (roleName?: string | null) => (roleName === 'store_admin' ? 6 : 4);
export const currentPinLength = (emp: StoreEmployee) => Number(emp.pin_length) || pinLengthFor(emp.role_name);
/** PIN με άλλο μήκος από όσο θέλει ο ρόλος (π.χ. ταμίας που έγινε διαχειριστής πριν από τις 12/10). */
export const pinMismatch = (emp: StoreEmployee) =>
  !!emp.has_pin && !isHqRole(emp.role_name) && currentPinLength(emp) !== pinLengthFor(emp.role_name);

/** Οι ρόλοι που προσφέρει η φόρμα σε ΑΥΤΟ το κατάστημα (το φίλτρο ονομάτων μένει και αν ο server είναι παλιός). */
export function offeredRoles(roles: RoleOption[], store: StoreFlags | null | undefined): RoleOption[] {
  return roles
    .filter(r => isOfferedRole(r.name))
    .filter(r => r.name !== 'kitchen' || !!store?.dine_in)
    .filter(r => r.name !== 'kiosk' || !!store?.kiosk_enabled)
    .sort((a, b) => ROLE_ORDER.indexOf(a.name) - ROLE_ORDER.indexOf(b.name));
}

export const fullName = (e: Pick<StoreEmployee, 'first_name' | 'last_name'>) => `${e.first_name || ''} ${e.last_name || ''}`.trim();

/** Ενεργοί πρώτα, μετά κατά ρόλο (διαχειριστές πάνω), μετά αλφαβητικά. */
export function sortEmployees(list: StoreEmployee[]): StoreEmployee[] {
  const rank = (name: string) => {
    const i = ROLE_ORDER.indexOf(name);
    return i === -1 ? ROLE_ORDER.length : i;
  };
  return [...list].sort((a, b) =>
    Number(b.is_active) - Number(a.is_active)
    || rank(a.role_name) - rank(b.role_name)
    || fullName(a).localeCompare(fullName(b), 'el'));
}

/* ───────────────────────── δικαιώματα ───────────────────────── */

export interface PermissionDef { key: string; defaultTrue?: boolean }
export type PermissionSectionId = 'basic' | 'dineIn' | 'orders' | 'fiscal' | 'catalog';
export interface PermissionSection { id: PermissionSectionId; items: PermissionDef[] }

// Βασικά του σερβιτόρου: ανοιχτά εκτός αν κλείσουν ρητά (ο ταμίας τα έχει πάντα).
export const WAITER_OPTIONAL_PERMISSION_KEYS = ['dine_in_print_receipt', 'dine_in_print_invoice', 'dine_in_process_payment'];

export const DINE_IN_PERMISSION_KEYS: PermissionDef[] = [
  { key: 'dine_in_correction_order' },
  { key: 'dine_in_move_table' },
  { key: 'dine_in_apply_discount' },
  { key: 'dine_in_treat' },
  { key: 'dine_in_cancel_order' },
  { key: 'dine_in_cancel_after_receipt' },
  { key: 'dine_in_void_payment' },
  { key: 'dine_in_view_registers', defaultTrue: true },
];

export const ORDER_PERMISSION_KEYS: PermissionDef[] = [
  { key: 'view_orders_tab', defaultTrue: true },
  { key: 'view_platform_order_popups', defaultTrue: true },
  { key: 'takeaway_apply_discount' },
  { key: 'delivery_apply_discount' },
  { key: 'view_call_history' },
  { key: 'view_customers' },
  // cancel_orders: μόνο όσες εισπράττει η πλατφόρμα· cancel_all_orders: και όσες εισέπραξε το κατάστημα.
  { key: 'cancel_orders' },
  { key: 'cancel_all_orders' },
];

export const FISCAL_PERMISSION_KEYS: PermissionDef[] = [
  { key: 'view_fiscal_documents' },
  { key: 'retry_fiscal_document' },
  { key: 'close_register' },
  { key: 'register_x_report', defaultTrue: true },
  // Κινήσεις ταμείου (migration 277): καταχώριση ανοιχτή για όλους (όπως η Χ), ακύρωση μόνο με ρητό δικαίωμα
  // (όπως το κλείσιμο) — ίδιοι κανόνες με τον server (services/cashMovements canCreate / canVoid).
  { key: 'register_cash_movement', defaultTrue: true },
  { key: 'register_cash_movement_void' },
];

// Πιάτα ημέρας (migration 278, απόφαση ιδιοκτήτη 20/10/2026): ποια πουλιούνται σήμερα, τιμή, νέο πιάτο και
// «Καθαρισμός ημέρας» από το μενού του ταμείου. Ειδικό: μόνο με ρητό true (όπως στον server, services/dailyDishes).
export const CATALOG_PERMISSION_KEYS: PermissionDef[] = [
  { key: 'daily_dishes_manage' },
];

export const ALL_PERMISSION_KEYS = [
  ...WAITER_OPTIONAL_PERMISSION_KEYS,
  ...[...DINE_IN_PERMISSION_KEYS, ...ORDER_PERMISSION_KEYS, ...FISCAL_PERMISSION_KEYS, ...CATALOG_PERMISSION_KEYS].map(p => p.key),
];

/** Οι ενότητες για τον ρόλο. Τα τραπέζια μόνο σε κατάστημα με τραπέζια (αλλιώς δεν σημαίνουν τίποτα). */
export function permissionSections(roleName: string, store: StoreFlags | null | undefined): PermissionSection[] {
  if (!PERMISSION_ROLES.includes(roleName)) return [];
  const sections: PermissionSection[] = [];
  if (store?.dine_in) {
    if (roleName === 'waiter') {
      sections.push({ id: 'basic', items: WAITER_OPTIONAL_PERMISSION_KEYS.map(key => ({ key, defaultTrue: true })) });
    }
    sections.push({ id: 'dineIn', items: DINE_IN_PERMISSION_KEYS });
  }
  sections.push({ id: 'orders', items: ORDER_PERMISSION_KEYS });
  sections.push({ id: 'fiscal', items: FISCAL_PERMISSION_KEYS });
  // Μόνο με ανοιχτά τα «Πιάτα ημέρας» (κλειστή λειτουργία = το δικαίωμα δεν σημαίνει τίποτα).
  if (store?.daily_dishes_enabled === true) sections.push({ id: 'catalog', items: CATALOG_PERMISSION_KEYS });
  return sections;
}

/** Ό,τι ισχύει στον server: defaultTrue → !== false, αλλιώς === true. */
export const isPermissionOn = (perms: Perms | null | undefined, def: PermissionDef) =>
  def.defaultTrue ? perms?.[def.key] !== false : perms?.[def.key] === true;

/* ───────────────────────── φόρμα ───────────────────────── */

export interface EmployeeDraft {
  first_name: string;
  last_name: string;
  phone: string;
  role_id: number | null;
  pin: string;
  is_active: boolean;
  kiosk_terminal_id: number | null;
  /** Ό,τι άλλαξε ο χρήστης στα δικαιώματα (κλειδί → ανοιχτό/κλειστό). */
  permEdits: Record<string, boolean>;
  /** «Επαναφορά στα προεπιλεγμένα του ρόλου»: εφαρμόζεται με την αποθήκευση. */
  permsReset: boolean;
}

export function initialDraft(emp: StoreEmployee | null, roles: RoleOption[]): EmployeeDraft {
  if (!emp) {
    return {
      first_name: '', last_name: '', phone: '',
      role_id: roles.find(r => r.name === DEFAULT_ROLE)?.id ?? roles[0]?.id ?? null,
      pin: '', is_active: true, kiosk_terminal_id: null, permEdits: {}, permsReset: false,
    };
  }
  return {
    first_name: emp.first_name || '', last_name: emp.last_name || '', phone: emp.phone || '',
    role_id: emp.role_id, pin: '', is_active: emp.is_active, kiosk_terminal_id: emp.kiosk_terminal_id ?? null,
    permEdits: {}, permsReset: false,
  };
}

export interface FormContext {
  employee: StoreEmployee | null;
  /** Όλοι οι ρόλοι που ξέρει η φόρμα (για όνομα/προεπιλογές του επιλεγμένου). */
  roles: RoleOption[];
  store: StoreFlags | null | undefined;
}

export const selectedRoleName = (draft: EmployeeDraft, ctx: FormContext) =>
  (ctx.employee && draft.role_id === ctx.employee.role_id)
    ? ctx.employee.role_name
    : ctx.roles.find(r => r.id === draft.role_id)?.name || '';

/**
 * Από τι ξεκινούν τα δικαιώματα στην οθόνη: του ατόμου όπως είναι, ή — νέος υπάλληλος, άλλος ρόλος,
 * «Επαναφορά» — οι προεπιλογές του ρόλου (τα παλιά του άλλου ρόλου δεν μεταφέρονται).
 */
export function permissionBase(draft: EmployeeDraft, ctx: FormContext): { base: Perms; fresh: boolean } {
  const emp = ctx.employee;
  const fresh = !emp || draft.permsReset || draft.role_id !== emp.role_id;
  if (!fresh) return { base: emp!.permissions || {}, fresh };
  const role = ctx.roles.find(r => r.id === draft.role_id);
  return { base: (role?.permissions as Perms) || {}, fresh };
}

export function permissionView(draft: EmployeeDraft, ctx: FormContext) {
  const roleName = selectedRoleName(draft, ctx);
  const sections = permissionSections(roleName, ctx.store);
  const { base, fresh } = permissionBase(draft, ctx);
  const isOn = (def: PermissionDef) => (def.key in draft.permEdits ? draft.permEdits[def.key] : isPermissionOn(base, def));
  const changed = sections.flatMap(s => s.items).filter(def => def.key in draft.permEdits && draft.permEdits[def.key] !== isPermissionOn(base, def));
  return { roleName, sections, base, fresh, isOn, changed };
}

export interface FormErrors {
  first_name?: { key: string; digits?: number };
  role?: { key: string };
  pin?: { key: string; digits?: number };
}

/** Τι στέλνεται: ΜΟΝΟ ό,τι άλλαξε (ενημέρωση) ή ό,τι χρειάζεται (νέος). `dirty` = υπάρχει κάτι να σταλεί. */
export function buildPayload(draft: EmployeeDraft, ctx: FormContext): {
  body: Record<string, unknown>; dirty: boolean; errors: FormErrors; pinRequired: boolean; pinDigits: number;
} {
  const emp = ctx.employee;
  const view = permissionView(draft, ctx);
  const roleName = view.roleName;
  const pinDigits = pinLengthFor(roleName);
  const roleChanged = !!emp && draft.role_id !== emp.role_id;
  // Αλλαγή ρόλου που αλλάζει το μήκος PIN: το παλιό PIN δεν θα ταίριαζε στο πληκτρολόγιο.
  const pinRequired = !emp || (roleChanged && !!emp.has_pin && !isHqRole(roleName) && pinDigits !== currentPinLength(emp));
  const errors: FormErrors = {};
  const first = draft.first_name.trim();
  const pin = draft.pin.trim();
  if (!first) errors.first_name = { key: 'firstNameRequired' };
  if (!draft.role_id || !roleName) errors.role = { key: 'roleRequired' };
  if (pin && !new RegExp(`^\\d{${pinDigits}}$`).test(pin)) errors.pin = { key: 'pinDigits', digits: pinDigits };
  else if (!pin && pinRequired) errors.pin = { key: emp ? 'pinRequiredForRole' : 'pinRequired', digits: pinDigits };

  const body: Record<string, unknown> = {};
  const configurable = PERMISSION_ROLES.includes(roleName);
  const changedValues = Object.fromEntries(view.changed.map(def => [def.key, draft.permEdits[def.key]]));

  if (!emp) {
    body.first_name = first;
    body.last_name = draft.last_name.trim();
    body.phone = draft.phone.trim();
    body.role_id = draft.role_id;
    body.pin_code = pin;
    if (roleName === 'kiosk') body.kiosk_terminal_id = draft.kiosk_terminal_id ?? null;
    if (configurable && view.changed.length) body.permissions = changedValues;
    return { body, dirty: true, errors, pinRequired, pinDigits };
  }

  if (first !== (emp.first_name || '').trim()) body.first_name = first;
  if (draft.last_name.trim() !== (emp.last_name || '').trim()) body.last_name = draft.last_name.trim();
  if (draft.phone.trim() !== (emp.phone || '').trim()) body.phone = draft.phone.trim();
  if (roleChanged) body.role_id = draft.role_id;
  if (pin) body.pin_code = pin;
  if (draft.is_active !== emp.is_active) body.is_active = draft.is_active;
  if (roleName === 'kiosk') {
    if ((draft.kiosk_terminal_id ?? null) !== (emp.kiosk_terminal_id ?? null)) body.kiosk_terminal_id = draft.kiosk_terminal_id ?? null;
  } else if (roleChanged && emp.kiosk_terminal_id != null) {
    body.kiosk_terminal_id = null;
  }

  if (configurable && view.changed.length) {
    // Άλλος ρόλος / επαναφορά: μόνο οι αλλαγές πάνω στις προεπιλογές του ρόλου. Αλλιώς ό,τι είχε το
    // άτομο + οι αλλαγές (κλειδιά που η οθόνη δεν δείχνει μένουν όπως ήταν).
    body.permissions = view.fresh ? changedValues : { ...(emp.permission_overrides || {}), ...changedValues };
  } else if (view.fresh && emp.permission_overrides != null) {
    body.permissions = null;
  }
  return { body, dirty: Object.keys(body).length > 0, errors, pinRequired, pinDigits };
}

/** Μήνυμα για κωδικό σφάλματος του server (routes/storeAdmin.js). */
export function errorKeyForCode(code?: string): string | null {
  switch (code) {
    case 'EMPLOYEE_EMAIL_TAKEN': return 'emailTaken';
    case 'INVALID_PIN': return 'pinDigits';
    case 'PIN_REQUIRED_FOR_ROLE': return 'pinRequiredForRole';
    case 'CANNOT_CHANGE_OWN_ROLE': return 'ownRole';
    case 'CANNOT_DEACTIVATE_SELF': return 'selfDeactivate';
    case 'INVALID_KIOSK_TERMINAL': return 'kioskTerminal';
    case 'PERMISSION_NOT_ALLOWED': return 'permissionNotAllowed';
    case 'FIRST_NAME_REQUIRED': return 'firstNameRequired';
    default: return null;
  }
}
